import { useStore } from "@nanostores/react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import {
  dataStore,
  filteredValuesStore,
  fixedValuesStore,
  getSearchBasePath,
  selectedValueStore,
  setTypeValues,
  updateUrl,
} from "../../_utils/searchStore";
import type { Value } from "../../_utils/types";
import { Checkbox } from "./Checkbox";
import { Collapse } from "./Collapse";
import { MultiSelect } from "./MultiSelect";
import { drawerTypeStore } from "./store";

type Props = {
  isDesktop?: boolean;
  fit?: boolean;
};
export const Themes = ({ isDesktop = false, fit = false }: Props) => {
  const router = useRouter();
  const state = useStore(drawerTypeStore);
  const selectedThemes = useStore(filteredValuesStore("themes"));
  const [localThemes, setLocalThemes] = useState(selectedThemes);
  const selectedThemesRef = useRef(selectedThemes);
  const data = useStore(dataStore);

  useEffect(() => {
    const isDiff = () => {
      const oldT = selectedThemesRef.current.sort((a, b) => a.value.localeCompare(b.value));

      const newT = selectedThemes.sort((a, b) => a.value.localeCompare(b.value));

      if (oldT.length !== newT.length) return true;

      for (let index = 0; index < newT.length; index++) {
        if (oldT[index] !== newT[index]) return true;
      }

      return false;
    };

    if (isDiff()) {
      selectedThemesRef.current = selectedThemes;
      setLocalThemes(selectedThemes);
    }
  }, [selectedThemes]);

  const handleChange = (newValue: Value, directUpdate = false) => {
    let newThemes: Array<Value> = [];
    if (localThemes.some((lt) => lt.value === newValue.value)) {
      newThemes = localThemes.filter((lt) => lt.value !== newValue.value);
    } else {
      newThemes = [...localThemes, newValue];
    }

    setLocalThemes(newThemes);

    if (directUpdate) {
      if (
        fixedValuesStore
          .get()
          .flat()
          .some((lt) => lt.value === newValue.value)
      ) {
        router.push(getSearchBasePath());
        return;
      }

      if (selectedValueStore.get()?.value === newValue.value) {
        selectedValueStore.set(null);
      }

      setTypeValues("themes", newThemes);
      updateUrl(router);
    }
  };

  const handleOpen = () => {
    drawerTypeStore.set(state === "themes" ? null : "themes");
  };

  const handleClickOutside = () => {
    setTypeValues("themes", localThemes);

    drawerTypeStore.set(null);
  };

  const handleValidate = () => {
    setTypeValues("themes", localThemes);
    updateUrl(router);
  };

  const handleClear = () => {
    setTypeValues("themes", []);
    updateUrl(router);
  };

  const themes = [
    ...selectedThemes
      .filter((s) => !data?.themes.some((t) => t.key === s.value))
      .map((s) => ({
        value: s.value,
      })),
    ...(data?.themes.map((t) => ({ value: t.key })) || []),
  ];

  return isDesktop ? (
    <MultiSelect
      fit={fit}
      placeholder="Thématiques"
      type="themes"
      onChange={handleChange}
      onValidate={handleValidate}
      onClear={handleClear}
      localValues={localThemes}
      selectedValues={selectedThemes}
      values={themes.map((d) => ({ label: d.value, value: d.value }))}
    />
  ) : (
    <Collapse
      open={state === "themes"}
      title="Thématiques"
      onclick={handleOpen}
      onClickOutside={handleClickOutside}
      count={selectedThemes.length}
    >
      <div className="mt-5 grid sm:grid-cols-2 gap-[10px]">
        {themes
          .sort((a, b) => a.value.localeCompare(b.value))
          .map((theme) => (
            <Checkbox
              key={theme.value}
              label={theme.value}
              checked={localThemes.some((lt) => lt.value === theme.value)}
              onChange={() =>
                handleChange({
                  type: "themes",
                  value: theme.value,
                })
              }
            />
          ))}
      </div>
    </Collapse>
  );
};
